"use client"

import { useExitIntent } from "@/hooks/useExitIntent"
import { isProfileCompleteStrict } from "@/utils/helpers/utils"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { match } from "ts-pattern"
import CompleteProfile from "../Forms/CompleteProfile/complete-profile"
import ExitIntent from "../Modal/ExitIntent"
import VerifyEmail from "../Modal/VerifyEmail"
import { DesktopNavBar } from "./desktop/navbar"
import { MobileNavBar } from "./mobile/navbar"
import { useAuthState } from "@/hooks/useAuthState"
import { useVerificationReminderCheck } from "@/hooks/useUpdateVerification"
import { isReminderWindowActive, markReminderShown } from "@/utils/verify-reminder/local-window"

export function Header({
   latestVideoSlug,
   playlists,
   canAdoptSso = false,
   enableExtraSpace = true,
}: {
   latestVideoSlug: string | undefined
   playlists: string[]
   /** Server-read: could a silent SSO probe still turn "logged out" into "logged in"? */
   canAdoptSso?: boolean
   enableExtraSpace?: boolean
}) {
   const ref = useRef<any>(null)
   const pathname = usePathname()
   const router = useRouter()
   // One source of truth for auth, shared with both navbars — no local settle timer, and no
   // chance of the desktop and mobile navbars disagreeing.
   const { session, user, isAuthenticated, isResolving } = useAuthState({ canAdoptSso })

   const [showExitModal, setShowExitModal] = useState(false)
   const [hasShownModal, setHasShownModal] = useState(false)
   const [profileNeedsCompletion, setProfileNeedsCompletion] = useState(false)
   const [profileChecked, setProfileChecked] = useState(false)

   const [showVerifyModal, setShowVerifyModal] = useState(false)
   // Guards against re-asking raauth for the reminder window on every re-render.
   const reminderCheckedRef = useRef(false)

   const searchParams = useSearchParams()
   const redirectUrl = searchParams.get("redirect")
   const callbackUrl = searchParams.get("callbackUrl")

   const reminderCheck = useVerificationReminderCheck()

   // --- 24-Hour Email Verification reminder ---
   // The once-per-24h cadence now lives in raauth (verification_reminder policy);
   // we ask it whether to show the nag rather than tracking a local timestamp.
   useEffect(() => {
      if (isResolving || !isAuthenticated || !user || !!user.emailVerified || pathname?.includes("unified")) return
      if (reminderCheckedRef.current) return

      // Per-user client cooldown: survives sign-out/login and browser reopen (and covers
      // users not yet linked to raauth, which the server-side window can't gate). Skip
      // entirely while active so we neither show the nag nor consume the raauth window.
      const userKey = user.id ?? user.email ?? ""
      if (isReminderWindowActive(userKey)) {
         reminderCheckedRef.current = true
         return
      }

      const timer = setTimeout(async () => {
         reminderCheckedRef.current = true
         try {
            const { show } = await reminderCheck.mutateAsync()
            if (show) {
               setShowVerifyModal(true)
               markReminderShown(userKey)
            }
         } catch {
            // Fail-soft: a reminder-service hiccup must never block the page.
         }
      }, 10000)

      return () => clearTimeout(timer)
   }, [isAuthenticated, isResolving, user, pathname, reminderCheck])

   useEffect(() => {
      // Only run on mobile devices
      const isMobile = typeof window !== "undefined" && window.innerWidth < 1024
      if (!isMobile) return

      // After 90 seconds of inactivity, show the exit intent modal
      const timer = setTimeout(() => {
         if (!hasShownModal) {
            setShowExitModal(true)
            setHasShownModal(true)
         }
      }, 90000)

      return () => clearTimeout(timer)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // Custom exit intent hook
   const { resetTrigger } = useExitIntent({
      delayInSeconds: 10,
      onExitIntent: () => {
         if (!hasShownModal) {
            setShowExitModal(true)
            setHasShownModal(true)
         }
      },
      sensitivity: 10,
   })

   // Handles the closing of the modal
   const handleCloseModal = () => {
      setShowExitModal(false)
      resetTrigger()
   }

   // Handles the incomplete profile
   const handleRedirectCompleteProfile = () => {
      const token = match(redirectUrl)
         .with("training", () => session?.training_session)
         .with("jobs", () => session?.jobs_session)
         .with("webinars", () => session?.webinars_session)
         .otherwise(() => null)

      if (session && redirectUrl && token && token !== "") {
         const redirectPath = `https://${redirectUrl}.restaurantassociation.com?session=${token}`
         router.push(redirectPath)
      }
   }

   // Guard to avoid multiple redirects
   const hasProfileRedirected = useRef(false)

   // Derive completion need from strict selector
   useEffect(() => {
      setProfileNeedsCompletion(!!(user && isAuthenticated && !isProfileCompleteStrict(user)))
      setProfileChecked(true)
   }, [user, isAuthenticated])

   useEffect(() => {
      if (!profileChecked) return
      if (!isAuthenticated || !session) return
      if (!redirectUrl) return
      if (!isProfileCompleteStrict(user)) return
      if (hasProfileRedirected.current) return

      hasProfileRedirected.current = true
      handleRedirectCompleteProfile()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [profileChecked, isAuthenticated, session, redirectUrl, callbackUrl, user])

   //Hide secondaryNavbar
   const hiddenBasePaths = [
      "/login",
      "/signup",
      "/unified",
      "/search",
      "/about",
      "/staff",
      "/contact",
      "/legal",
      "/disclosure-policy",
      "/review-guidelines",
      "/ethics",
      "/benefits",
      "/profile",
   ]
   const isSecondaryHidden = pathname === "/" || hiddenBasePaths.some((path) => pathname.startsWith(path))

   return (
      <>
         <header
            className={`relative z-[100] w-full overflow-visible border ${!isSecondaryHidden && enableExtraSpace ? "mb-4" : ""}`}
            ref={ref}
         >
            <div className="hidden lg:block">
               <DesktopNavBar
                  isAuthenticated={isAuthenticated}
                  isSessionLoading={isResolving}
                  user={user}
                  latestVideoSlug={latestVideoSlug ?? ""}
                  playlists={playlists}
                  isSecondaryHidden={isSecondaryHidden}
               />
            </div>
            <div className="block lg:hidden">
               <MobileNavBar
                  isAuthenticated={isAuthenticated}
                  isSessionLoading={isResolving}
                  user={user}
                  latestVideoSlug={latestVideoSlug ?? ""}
               />
            </div>
         </header>
         <div className={isSecondaryHidden ? "h-[50px]" : "h-[75px] md:h-[98px]"} />

         {!pathname?.includes("unified") && (
            <>
               <ExitIntent
                  isOpen={showExitModal && !isAuthenticated && status !== "loading"}
                  onClose={handleCloseModal}
               />

               {profileNeedsCompletion ? (
                  <CompleteProfile />
               ) : (
                  <VerifyEmail isOpen={showVerifyModal} onClose={() => setShowVerifyModal(false)} user={user} />
               )}
            </>
         )}
      </>
   )
}
